import { useState } from 'react'
import useNetwork from '../../models/useNetwork'
import ModalNetwork from '../ModalNetwork'
import CreateRefusal from '../CreateRefusal'
import DiagramFrame from './DiagramFrame'
import DiagramNode from './DiagramNode'
import Typography from '@mui/material/Typography'
import NextPrev from './NextPrev'
import Grid from '@mui/material/Grid'
import { limitRefusal, type LimitRefusal } from '../../utilities/limitRefusal'

export default function OnboardNetwork() {
  const { networks, add } = useNetwork()
  const network = networks[0]
  const [name, setName] = useState(network?.name ?? '')
  const [refusal, setRefusal] = useState<Pick<LimitRefusal, 'message' | 'upgradeUrl'> | null>(null)

  // ⚠ RE-THROWN ON PURPOSE (app#296). `NextPrev` navigates only when `onNext` resolves, so
  // swallowing the refusal here would carry a customer to "Add a node" for a network that was
  // never created. The refusal is rendered beside the button; the throw is what keeps the step.
  const onNext = async () => {
    setRefusal(null)
    try {
      await add({ name: name || 'default' })
    } catch (e) {
      console.error('network_create failed', e)
      setRefusal(limitRefusal(e) ?? { message: e instanceof Error ? e.message : String(e) })
      throw e
    }
  }

  return (
    <>
      <Grid size={{ xs: 12, sm: 3.5 }} />
      <Grid size={{ xs: 12, sm: 8.5 }}>
        <Typography variant="h1">Add a network</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 3.5 }} sx={{
        paddingTop: 4
      }}>
        <Typography variant="h5">
          Networks allow nodes to securely connect to one another, and share a drive.{' '}
          <Typography
            variant="code"
            component="span"
            sx={{
              fontStyle: 'normal',
              color: 'primary.main'
            }}>
            /mnt/nobgp
          </Typography>
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8.5 }}>
        <DiagramFrame
          left={<DiagramNode label="Language Model" type="llm" />}
          middle={<DiagramNode type="network" label={name} currentColor offline={!name} />}
          right={<DiagramNode label="Compute Node" type="node" />}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 3.5 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            marginBottom: 3
          }}>
          Create your first network
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8.5 }}>
        <ModalNetwork onChange={name => setName(name)} inline />
      </Grid>
      <Grid
        size={12}
        sx={{
          marginTop: 4,
          textAlign: 'right'
        }}>
        <CreateRefusal message={refusal?.message} upgradeUrl={refusal?.upgradeUrl} />
        <NextPrev prev="/onboard/welcome" next="/onboard/node" auto={networks} onNext={onNext} />
      </Grid>
    </>
  )
}
